// Admin → Settings → Stripe. Read-only view of which Stripe keys are
// configured server-side (.env), plus the testing-bypass toggle stored in
// site_settings.stripe_config.testingBypass. When ON, new customer orders
// skip Stripe entirely and land straight as 'paid' → production queue.
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { useToast, ToastBanner } from '../../components/Toast'

interface PaymentsConfig { configured: boolean; publishableKey: string | null }
interface StripeSettings { testingBypass: boolean }

export default function SettingsStripe() {
  const { getAccessToken } = useAuth()
  const [pay, setPay] = useState<PaymentsConfig | null>(null)
  const [settings, setSettings] = useState<StripeSettings>({ testingBypass: false })
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle')
  const { toast, show } = useToast()

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([
          apiFetch<PaymentsConfig>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/payments/config'),
          apiFetch<{ value: StripeSettings | null }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/settings/stripe_config'),
        ])
        setPay(p)
        setSettings({ testingBypass: !!s.value?.testingBypass })
      } catch (e) { setErr((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function save() {
    setSaving('saving')
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/settings/stripe_config', {
        method: 'PUT', body: JSON.stringify({ value: settings }),
      })
      setSaving('saved')
      show(settings.testingBypass ? 'Stripe testing bypass is ON — new orders skip payment.' : 'Stripe settings saved.')
    } catch (e) { setSaving('idle'); show((e as Error).message, 'error', 5000) }
  }

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <div className="mb-6">
        <div className="eyebrow">Settings</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Stripe</h1>
        <p className="text-ink-2 text-sm mt-1 max-w-2xl">Payment integration. API keys live server-side in <span className="font-mono text-xs">apps/api/.env</span>; this page shows their config status and lets you toggle a testing bypass for end-to-end runs without taking real payment.</p>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      {/* Key status */}
      <div className="card max-w-3xl mb-6">
        <div className="eyebrow">Server configuration</div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-ink-2">Stripe Secret Key</span>
            {pay?.configured
              ? <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Configured</span>
              : <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Not set</span>}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-ink-2">Publishable Key</span>
            <span className="font-mono text-xs text-ink-3 truncate max-w-[260px]">{pay?.publishableKey ?? 'Not set'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-ink-2">Webhook Endpoint</span>
            <span className="font-mono text-xs text-ink-3 truncate max-w-[300px]">https://prints.evrststudio.com/api/webhooks/stripe</span>
          </div>
          <div className="flex items-start justify-between gap-4">
            <span className="text-ink-2 whitespace-nowrap">Subscribe events</span>
            <span className="font-mono text-[11px] text-ink-3 text-right">
              checkout.session.completed<br />
              checkout.session.async_payment_succeeded<br />
              charge.refunded
            </span>
          </div>
        </div>
        <p className="text-[11px] text-ink-3 mt-3">To activate or rotate keys, set <span className="font-mono">STRIPE_SECRET_KEY</span>, <span className="font-mono">STRIPE_PUBLISHABLE_KEY</span>, and <span className="font-mono">STRIPE_WEBHOOK_SECRET</span> in <span className="font-mono">/etc/app-secrets/evrst-prints.env</span>, add the webhook above with those three events, then rebuild the container. Full steps in <span className="font-mono">STRIPE.md</span>.</p>
      </div>

      {/* Bypass toggle */}
      <div className="card max-w-3xl">
        <div className="eyebrow">Testing mode</div>
        <div className="flex items-start gap-3">
          <button
            onClick={() => setSettings((s) => ({ ...s, testingBypass: !s.testingBypass }))}
            className={'relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ' + (settings.testingBypass ? 'bg-gold' : 'bg-border-2')}
            title="Toggle payment bypass"
          >
            <span className={'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ' + (settings.testingBypass ? 'translate-x-6' : '')} />
          </button>
          <div className="flex-1">
            <div className="text-ink font-semibold text-sm">Bypass payment on new orders</div>
            <p className="text-ink-2 text-xs mt-1">
              When ON, every new customer order is marked <strong className="text-ink">paid</strong> immediately and goes straight into the production queue — no Stripe Checkout, no card needed. Useful for end-to-end testing the production workflow without burning Stripe-test sessions.
            </p>
            <p className="text-[11px] text-amber-700 mt-2"><strong>Don't leave this on in production.</strong> Customers won't be charged.</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
          <div className="text-xs text-ink-3">{saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved' : ''}</div>
          <button onClick={save} className="btn-gold">Save</button>
        </div>
      </div>
    </div>
  )
}
